// src/redux/slices/laborSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../UtilsRedux/axiosWithAuth';
import { API_ROUTES } from '../../Constants/apiRoutes';
import { showFeedback } from '../../UI/uiFeedbackSlice';
import { fetchExpensesByProject } from '../Expense/expenseSlice';
// adjust relative path


// ---------------------------
// 🔄 Async Thunks
// ---------------------------

// Workers
export const fetchWorkers = createAsyncThunk(
  'labor/fetchWorkers',
  async (projectId, { rejectWithValue, dispatch }) => {
    try {
      const res = await axios.get(API_ROUTES.GET_WORKERS_BY_PROJECT(projectId));
      return res.data.data; // ✅ matches controller
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch workers';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Could not load workers',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

export const createWorker = createAsyncThunk(
  'labor/createWorker',
  async (workerData, { rejectWithValue, dispatch }) => {
    try {
      const res = await axios.post(API_ROUTES.CREATE_WORKER, workerData);
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Worker added',
          message: 'The worker has been created successfully.',
        })
      );

      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to create worker';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Create worker failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

export const updateWorker = createAsyncThunk(
  'labor/updateWorker',
  async ({ workerId, updates }, { rejectWithValue, dispatch }) => {
    try {
      const res = await axios.put(API_ROUTES.UPDATE_WORKER(workerId), updates);
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Worker updated',
          message: 'Changes have been saved.',
        })
      );

      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to update worker';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Update worker failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

export const deleteWorker = createAsyncThunk(
  'labor/deleteWorker',
  async (workerId, { rejectWithValue, dispatch }) => {
    try {
      await axios.delete(API_ROUTES.DELETE_WORKER(workerId));
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Worker deleted',
          message: 'The worker has been removed.',
        })
      );

      return workerId;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to delete worker';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Delete worker failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

// Attendance
export const markBulkAttendance = createAsyncThunk(
  'labor/markBulkAttendance',
  async (records, { rejectWithValue, dispatch }) => {
    try {
      const res = await axios.post(API_ROUTES.MARK_ATTENDANCE_BULK, { records });
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Attendance saved',
          message: 'Bulk attendance has been recorded.',
        })
      );

      const projectId = records?.[0]?.projectId;
      if (projectId) await dispatch(refreshLaborProject(projectId));


      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to mark bulk attendance';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Bulk attendance failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

export const markIndividualAttendance = createAsyncThunk(
  'labor/markIndividualAttendance',
  async ({ workerId, data }, { rejectWithValue, dispatch }) => {
    try {
      const res = await axios.post(
        API_ROUTES.MARK_ATTENDANCE_INDIVIDUAL(workerId),
        data
      );

      dispatch(showFeedback({
        type: 'success',
        title: 'Attendance updated',
        message: 'Attendance has been recorded for this worker.',
      }));

      if (data.projectId) await dispatch(refreshLaborProject(data.projectId));

      return res.data.data;
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to mark attendance';
      dispatch(showFeedback({ type: 'error', title: 'Attendance update failed', message }));
      return rejectWithValue(message);
    }
  }
);


export const getWorkerAttendance = createAsyncThunk(
  'labor/getWorkerAttendance',
  async (workerId, { rejectWithValue, dispatch }) => {
    try {
      const res = await axios.get(API_ROUTES.GET_WORKER_ATTENDANCE(workerId));
      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch attendance';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Could not load attendance',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);


export const fetchAttendanceByDate = createAsyncThunk(
  'labor/fetchAttendanceByDate',
  async ({ projectId, date }, { rejectWithValue, dispatch }) => {
    try {
      const res = await axios.get(API_ROUTES.FETCH_ATTENDANCE_BY_DATE(projectId, date));
      return res.data.data; // Array of { workerId, status }
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch attendance';

      dispatch(
        showFeedback({
          type: 'warning',
          title: 'Could not load attendance',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);



export const logWorkerPayment = createAsyncThunk(
  'labor/logWorkerPayment',
  async ({ workerId, data }, { rejectWithValue, dispatch }) => {
    try {
      const res = await axios.post(`/labor/payment/${workerId}`, data);
      const payload = res.data.data; // { payment, expense, updatedOutstanding, ... }

      // 🔔 Better message based on whether expense was created
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Payment logged',
          message: payload.expense
            ? 'Payment logged and Expense entry created.'
            : 'Payment has been recorded for this worker.',
        })
      );

      // ✅ If an Expense was created, refetch that project's expenses
      // ✅ If an Expense was created, refetch that project's expenses
      if (payload.expense && payload.expense.projectId) {
        dispatch(
          fetchExpensesByProject({
            projectId: payload.expense.projectId,
            page: 1,          // keep in sync with your Expense tab defaults
            pageSize: 20,
            filters: {},      // or carry over current filters later if you want
          })
        );
      }

      // keep drawer data fresh too
      dispatch(getWorkerPayments(workerId));

      // ✅ THIS is what fixes “need reload”
      if (data.projectId) await dispatch(refreshLaborProject(data.projectId));

      return { workerId, ...payload };
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Payment logging failed';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Payment failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);





export const getWorkerPayments = createAsyncThunk(
  'labor/getWorkerPayments',
  async ({ workerId, projectId }, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        API_ROUTES.GET_WORKER_PAYMENTS(workerId, projectId)
      );
      return { workerId, payments: res.data.data }; // ✅ keep full docs
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || 'Failed to fetch payments');
    }
  }
);




// 🔄 GET Labor Summary
export const getLaborSummary = createAsyncThunk(
  'labor/getLaborSummary',
  async (projectId, thunkAPI) => {
    try {
      const res = await axios.get(`/labor/project/${projectId}/summary`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error fetching summary');
    }
  }
);

// 💰 GET Outstanding Workers
export const getOutstandingWorkers = createAsyncThunk(
  'labor/getOutstandingWorkers',
  async (projectId, thunkAPI) => {
    try {
      const res = await axios.get(`/labor/project/${projectId}/outstanding`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error fetching outstanding workers');
    }
  }
);








export const fetchRecentActivity = createAsyncThunk(
  'labor/fetchRecentActivity',
  async (projectId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_ROUTES.GET_RECENT_LABOR_ACTIVITY(projectId)}`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch activity');
    }
  }
);


export const refreshLaborProject = createAsyncThunk(
  'labor/refreshLaborProject',
  async (projectId, { dispatch }) => {
    await Promise.allSettled([
      dispatch(fetchWorkers(projectId)),
      dispatch(getOutstandingWorkers(projectId)),
      dispatch(getLaborSummary(projectId)),
      dispatch(fetchRecentActivity(projectId)),
    ]);
    return true;
  }
);





// ---------------------------
// 🧠 Slice
// ---------------------------

const laborSlice = createSlice({
  name: 'labor',
  initialState: {
    workers: [],
    attendance: {},
    attendanceByDate: {},
    loadingAttByDate: false,
    payments: {},
    loading: false,
    error: null,
    recentActivity: [],
    summary: {
      totalWorkers: 0,
      totalPresentDays: 0,
      outstandingAmount: 0,
      loadingSum: false,
      error: null
    },
    outstanding: {
      list: [],
      loadingOut: false,
      error: null
    }
  },
  reducers: {
    clearLaborError: (state) => {
      state.error = null;
      state.summary.error = null;
      state.outstanding.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Workers
      .addCase(fetchWorkers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWorkers.fulfilled, (state, action) => {
        state.loading = false;
        state.workers = action.payload;
      })
      .addCase(fetchWorkers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createWorker.fulfilled, (state, action) => {
        state.workers.unshift(action.payload);
      })
      .addCase(updateWorker.fulfilled, (state, action) => {
        const index = state.workers.findIndex(w => w._id === action.payload._id);
        if (index !== -1) state.workers[index] = action.payload;
      })
      .addCase(deleteWorker.fulfilled, (state, action) => {
        state.workers = state.workers.filter(w => w._id !== action.payload);
      })

      .addCase(getWorkerAttendance.fulfilled, (state, action) => {
        const workerId = action.meta.arg;
        const rawArray = action.payload;

        const transformed = {};
        rawArray.forEach((entry) => {
          if (entry?.date) transformed[entry.date] = entry.status; // ✅ no Date() conversion
        });

        state.attendance[workerId] = transformed;
      })



      .addCase(logWorkerPayment.fulfilled, (state, action) => {
        const { workerId, updatedOutstanding } = action.payload;
        const worker = state.outstanding.list.find(w => w.workerId === workerId);
        if (worker) worker.outstanding = updatedOutstanding;
      })


      // Payments
      .addCase(getWorkerPayments.fulfilled, (state, action) => {
        const { workerId, payments } = action.payload;
        state.payments = state.payments || {};
        state.payments[workerId] = payments; // ✅ receiptUrl stays
      })



      .addCase(markIndividualAttendance.fulfilled, (state, action) => {
        const attendanceRecord = action.payload;

        if (
          attendanceRecord?.workerId &&
          attendanceRecord?.date &&
          attendanceRecord?.status
        ) {
          const workerId = attendanceRecord.workerId;
          const date = attendanceRecord.date;
          const status = attendanceRecord.status;

          if (!state.attendance[workerId]) {
            state.attendance[workerId] = {};
          }

          state.attendance[workerId][date] = status;
        }
      })


      .addCase(fetchAttendanceByDate.fulfilled, (state, action) => {
        const result = {};
        action.payload.forEach(({ workerId, status }) => {
          result[workerId] = status;
        });
        state.attendanceByDate = result;
        state.loadingAttByDate = false;
      })


      .addCase(fetchAttendanceByDate.rejected, (state) => {
        state.loadingAttByDate = false;
      })


      .addCase(getLaborSummary.pending, (state) => {
        state.summary.loading = true;
        state.summary.error = null;
      })
      .addCase(getLaborSummary.fulfilled, (state, action) => {
        state.summary.loading = false;
        state.summary = { ...state.summary, ...action.payload };
      })
      .addCase(getLaborSummary.rejected, (state, action) => {
        state.summary.loading = false;
        state.summary.error = action.payload;
      })


      .addCase(getOutstandingWorkers.pending, (state) => {
        state.outstanding.loadingOut = true;
        state.outstanding.error = null;
      })
      .addCase(getOutstandingWorkers.fulfilled, (state, action) => {
        state.outstanding.loadingOut = false;
        state.outstanding.list = action.payload;
      })
      .addCase(getOutstandingWorkers.rejected, (state, action) => {
        state.outstanding.loadingOut = false;
        state.outstanding.error = action.payload;
      })



      .addCase(fetchRecentActivity.fulfilled, (state, action) => {
        state.recentActivity = action.payload;
      });

  },
});

export const { clearLaborError } = laborSlice.actions;
export default laborSlice.reducer;
